/**
 * Japanese Prefectures (都道府県)
 *
 * All 47 prefectures with Kanji and Latin (romanized) names.
 * Used in the checkout delivery form for JP addresses.
 */

export interface Prefecture {
  /** Romanized code for API submission */
  code: string;
  /** Japanese name (Kanji) */
  kanji: string;
  /** Romanized name (Latin) */
  latin: string;
}

/**
 * All 47 Japanese prefectures in standard geographic order
 */
export const JP_PREFECTURES: Prefecture[] = [
  { code: 'hokkaido', kanji: '北海道', latin: 'Hokkaido' },
  { code: 'aomori', kanji: '青森県', latin: 'Aomori' },
  { code: 'iwate', kanji: '岩手県', latin: 'Iwate' },
  { code: 'miyagi', kanji: '宮城県', latin: 'Miyagi' },
  { code: 'akita', kanji: '秋田県', latin: 'Akita' },
  { code: 'yamagata', kanji: '山形県', latin: 'Yamagata' },
  { code: 'fukushima', kanji: '福島県', latin: 'Fukushima' },
  { code: 'ibaraki', kanji: '茨城県', latin: 'Ibaraki' },
  { code: 'tochigi', kanji: '栃木県', latin: 'Tochigi' },
  { code: 'gunma', kanji: '群馬県', latin: 'Gunma' },
  { code: 'saitama', kanji: '埼玉県', latin: 'Saitama' },
  { code: 'chiba', kanji: '千葉県', latin: 'Chiba' },
  { code: 'tokyo', kanji: '東京都', latin: 'Tokyo' },
  { code: 'kanagawa', kanji: '神奈川県', latin: 'Kanagawa' },
  { code: 'niigata', kanji: '新潟県', latin: 'Niigata' },
  { code: 'toyama', kanji: '富山県', latin: 'Toyama' },
  { code: 'ishikawa', kanji: '石川県', latin: 'Ishikawa' },
  { code: 'fukui', kanji: '福井県', latin: 'Fukui' },
  { code: 'yamanashi', kanji: '山梨県', latin: 'Yamanashi' },
  { code: 'nagano', kanji: '長野県', latin: 'Nagano' },
  { code: 'gifu', kanji: '岐阜県', latin: 'Gifu' },
  { code: 'shizuoka', kanji: '静岡県', latin: 'Shizuoka' },
  { code: 'aichi', kanji: '愛知県', latin: 'Aichi' },
  { code: 'mie', kanji: '三重県', latin: 'Mie' },
  { code: 'shiga', kanji: '滋賀県', latin: 'Shiga' },
  { code: 'kyoto', kanji: '京都府', latin: 'Kyoto' },
  { code: 'osaka', kanji: '大阪府', latin: 'Osaka' },
  { code: 'hyogo', kanji: '兵庫県', latin: 'Hyogo' },
  { code: 'nara', kanji: '奈良県', latin: 'Nara' },
  { code: 'wakayama', kanji: '和歌山県', latin: 'Wakayama' },
  { code: 'tottori', kanji: '鳥取県', latin: 'Tottori' },
  { code: 'shimane', kanji: '島根県', latin: 'Shimane' },
  { code: 'okayama', kanji: '岡山県', latin: 'Okayama' },
  { code: 'hiroshima', kanji: '広島県', latin: 'Hiroshima' },
  { code: 'yamaguchi', kanji: '山口県', latin: 'Yamaguchi' },
  { code: 'tokushima', kanji: '徳島県', latin: 'Tokushima' },
  { code: 'kagawa', kanji: '香川県', latin: 'Kagawa' },
  { code: 'ehime', kanji: '愛媛県', latin: 'Ehime' },
  { code: 'kochi', kanji: '高知県', latin: 'Kochi' },
  { code: 'fukuoka', kanji: '福岡県', latin: 'Fukuoka' },
  { code: 'saga', kanji: '佐賀県', latin: 'Saga' },
  { code: 'nagasaki', kanji: '長崎県', latin: 'Nagasaki' },
  { code: 'kumamoto', kanji: '熊本県', latin: 'Kumamoto' },
  { code: 'oita', kanji: '大分県', latin: 'Oita' },
  { code: 'miyazaki', kanji: '宮崎県', latin: 'Miyazaki' },
  { code: 'kagoshima', kanji: '鹿児島県', latin: 'Kagoshima' },
  { code: 'okinawa', kanji: '沖縄県', latin: 'Okinawa' },
];

/**
 * JP delivery time slot options
 */
export const JP_DELIVERY_TIME_SLOTS = [
  { value: '', label: '指定なし', latinLabel: 'No preference' },
  { value: 'morning', label: '午前中', latinLabel: 'Morning' },
  { value: '12-14', label: '12:00〜14:00', latinLabel: '12:00-14:00' },
  { value: '14-16', label: '14:00〜16:00', latinLabel: '14:00-16:00' },
  { value: '16-18', label: '16:00〜18:00', latinLabel: '16:00-18:00' },
  { value: '18-20', label: '18:00〜20:00', latinLabel: '18:00-20:00' },
  { value: '19-21', label: '19:00〜21:00', latinLabel: '19:00-21:00' },
];

/**
 * Get prefecture display name based on locale
 */
export function getPrefectureDisplayName(prefecture: Prefecture, locale?: string): string {
  const isJapanese = locale === 'ja' || locale?.startsWith('jp');
  return isJapanese ? prefecture.kanji : prefecture.latin;
}

/**
 * Get prefectures as StateProvince-compatible format for dropdown
 */
export function getJpPrefecturesForLocale(locale?: string): Array<{ code: string; name: string }> {
  return JP_PREFECTURES.map((p) => ({
    code: p.code,
    name: getPrefectureDisplayName(p, locale),
  }));
}

/**
 * Check if a locale represents Japan
 */
export function isJapanLocale(lang: string): boolean {
  return lang.startsWith('jp');
}

/**
 * Check if a country code is Japan
 */
export function isJapanCountry(countryCode: string): boolean {
  return countryCode === 'JP';
}
